/* =====================================================================
 * Team Store — 팀 목록/상세 전역 관리 (Zustand)
 * ===================================================================== */

import { create } from 'zustand';
import {
  listMyTeams,
  getTeamDetail,
  listTeamInvitations,
  type TeamInfo,
  type TeamMember,
  type TeamDetailResponse,
  type TeamInvitationResponse,
} from '@auw/api-client';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface TeamState {
  /** 내가 소속된 팀 목록 (사이드바 표시용) */
  teams: TeamInfo[];
  /** 팀 목록 로드 완료 여부 */
  isLoaded: boolean;

  /** AdminPage에서 선택한 팀 상세 */
  currentDetail: TeamDetailResponse | null;

  /** 팀 초대 목록 (AdminPage 초대 현황) */
  invitations: TeamInvitationResponse[];

  /** 메인 사이드패널 워크스페이스 뷰 (개인 | 특정 팀) */
  workspaceView: 'personal' | string;

  /** actions */
  loadMyTeams: () => Promise<void>;
  loadTeamDetail: (teamId: string) => Promise<void>;
  loadTeamInvitations: (teamId: string) => Promise<void>;
  applyTeamUsageUpdate: (payload: {
    teamId: string;
    tokensUsed?: number;
    tokensTotal?: number;
    storageUsedBytes?: number;
    storageTotalBytes?: number;
    myTokensAllocated?: number;
    myTokensUsed?: number;
    memberUserId?: string;
    memberTokensAllocated?: number;
    memberTokensUsed?: number;
  }) => void;
  setWorkspaceView: (view: 'personal' | string) => void;
  clearDetail: () => void;
  clear: () => void;
}

/* ------------------------------------------------------------------ */
/* Store                                                               */
/* ------------------------------------------------------------------ */

let loadMyTeamsInFlight: Promise<void> | null = null;

function upsertTeamSummary(teams: TeamInfo[], nextTeam: TeamInfo): TeamInfo[] {
  const existingIndex = teams.findIndex((team) => team.team_id === nextTeam.team_id);
  if (existingIndex === -1) {
    return [...teams, nextTeam];
  }

  const next = [...teams];
  next[existingIndex] = {
    ...next[existingIndex],
    ...nextTeam,
  };
  return next;
}

export const useTeamStore = create<TeamState>((set) => ({
  teams: [],
  isLoaded: false,
  currentDetail: null,
  invitations: [],
  workspaceView: 'personal',

  loadMyTeams: async () => {
    if (loadMyTeamsInFlight) return loadMyTeamsInFlight;

    loadMyTeamsInFlight = (async () => {
      try {
        const res = await listMyTeams();
        set({ teams: res.teams, isLoaded: true });
      } catch {
        set({ teams: [], isLoaded: true });
      } finally {
        loadMyTeamsInFlight = null;
      }
    })();

    return loadMyTeamsInFlight;
  },

  loadTeamDetail: async (teamId: string) => {
    try {
      const detail = await getTeamDetail(teamId);
      set((state) => ({
        currentDetail: detail,
        teams: upsertTeamSummary(state.teams, detail.team),
      }));
    } catch {
      set({ currentDetail: null });
    }
  },

  loadTeamInvitations: async (teamId: string) => {
    try {
      const res = await listTeamInvitations(teamId);
      set({ invitations: res.invitations });
    } catch {
      set({ invitations: [] });
    }
  },

  applyTeamUsageUpdate: ({
    teamId,
    tokensUsed,
    tokensTotal,
    storageUsedBytes,
    storageTotalBytes,
    myTokensAllocated,
    myTokensUsed,
    memberUserId,
    memberTokensAllocated,
    memberTokensUsed,
  }) => {
    if (!teamId) return;
    set((state) => {
      const nextTeams = state.teams.map((team) => (
        team.team_id === teamId
          ? {
              ...team,
              ...(typeof tokensUsed === 'number' ? { tokens_used: Math.max(0, tokensUsed) } : {}),
              ...(typeof tokensTotal === 'number' ? { tokens_total: Math.max(0, tokensTotal) } : {}),
              ...(typeof storageUsedBytes === 'number' ? { storage_used_bytes: Math.max(0, storageUsedBytes) } : {}),
              ...(typeof storageTotalBytes === 'number' ? { storage_total_bytes: Math.max(0, storageTotalBytes) } : {}),
              ...(typeof myTokensAllocated === 'number' ? { my_tokens_allocated: Math.max(0, myTokensAllocated) } : {}),
              ...(typeof myTokensUsed === 'number' ? { my_tokens_used: Math.max(0, myTokensUsed) } : {}),
            }
          : team
      ));

      let nextDetail = state.currentDetail;
      if (state.currentDetail?.team.team_id === teamId) {
        const nextMembers = (typeof memberUserId === 'string' && memberUserId)
          ? state.currentDetail.members.map((member) => (
            member.user_id === memberUserId
              ? {
                  ...member,
                  ...(typeof memberTokensAllocated === 'number'
                    ? { tokens_allocated: Math.max(0, memberTokensAllocated) }
                    : {}),
                  ...(typeof memberTokensUsed === 'number'
                    ? { tokens_used: Math.max(0, memberTokensUsed) }
                    : {}),
                }
              : member
          ))
          : state.currentDetail.members;

        nextDetail = {
          ...state.currentDetail,
          team: {
            ...state.currentDetail.team,
            ...(typeof tokensUsed === 'number' ? { tokens_used: Math.max(0, tokensUsed) } : {}),
            ...(typeof tokensTotal === 'number' ? { tokens_total: Math.max(0, tokensTotal) } : {}),
            ...(typeof storageUsedBytes === 'number' ? { storage_used_bytes: Math.max(0, storageUsedBytes) } : {}),
            ...(typeof storageTotalBytes === 'number' ? { storage_total_bytes: Math.max(0, storageTotalBytes) } : {}),
            ...(typeof myTokensAllocated === 'number' ? { my_tokens_allocated: Math.max(0, myTokensAllocated) } : {}),
            ...(typeof myTokensUsed === 'number' ? { my_tokens_used: Math.max(0, myTokensUsed) } : {}),
          },
          members: nextMembers,
        };
      }

      return { teams: nextTeams, currentDetail: nextDetail };
    });
  },

  setWorkspaceView: (view) => set({ workspaceView: view }),

  clearDetail: () => set({ currentDetail: null, invitations: [] }),

  clear: () => set({
    teams: [],
    isLoaded: false,
    currentDetail: null,
    invitations: [],
    workspaceView: 'personal',
  }),
}));
